// packaged-tv-scale.ts — âncora de escala do canvas em TV EMPACOTADA (file://).
//
// PROBLEMA (layout "espremido no centro"):
// O index.html é COMPARTILHADO entre web e TV. No web (http/https) o viewport
// `width=device-width` está certo. Já no WebView de TV empacotado (Tizen/webOS
// via file://), `device-width` NÃO amarra ao canvas físico 1920x1080 declarado
// no appinfo/config — o app renderiza numa largura lógica MENOR que a física e o
// shell da TV faz upscale/centraliza → tudo aparece "pequeno, espremido no
// centro". Todo o layout (41 arquivos com vw/vh) depende do viewport lógico
// bater com a física de 1920x1080.
//
// DEFESA EM DUAS CAMADAS:
//  - CAMADA 1 (âncora PRINCIPAL, no build): o vite.config.ts troca a meta
//    viewport pra `width=1920` SÓ no build de TV (`--mode tv`). Isso força o
//    viewport lógico a 1920 → os vw/vh voltam a bater. É o conserto real e
//    completo dos vw/vh. Não regride o web (build web mantém device-width).
//  - CAMADA 2 (rede de SEGURANÇA, este módulo, no boot): pra firmwares onde o
//    WebView IGNORA a meta viewport estática (reporta innerWidth ≠ 1920, tipo
//    960/1280/1024 — viewport default próprio do WebView). Duas sub-táticas
//    ordenadas, ativadas SÓ quando é TV empacotada E o viewport divergiu:
//      A) REFORÇAR a meta viewport via JS (barato, sem containing block,
//         conserta vw/vh de verdade se o firmware reagir à meta setada por JS).
//      B) se A não moveu o innerWidth, FALLBACK de escala: transform: scale()
//         no #root via a custom property --viewport-scale. Limitação conhecida
//         e documentada (ver applyPackagedTvScale): vw/vh residuais ficam
//         imperfeitos e overlays position:fixed podem vazar borda em device de
//         proporção não-16:9-exata — ainda MELHOR que "espremido no centro".
//
// CONSERVADOR: em device onde a meta viewport pegou (innerWidth == 1920, caso
// normal), a camada 2 é NO-OP — 1 leitura de innerWidth + 1 comparação, fim.
// Nenhum transform, nenhum containing block novo, zero overhead/risco no parque
// esmagador de firmwares modernos. "Verdade no boot" (não reage a resize — o
// mesmo princípio de resolveSurface(): trocar no meio remontaria a árvore).
//
// Núcleo em funções PURAS (isPackagedTv / computeViewportScale) — testável sem
// DOM; applyPackagedTvScale() é o glue que lê o ambiente real e aplica no boot.

import { detectPlatform } from '@app-iptv/device'

/** Largura de DESIGN do app: o canvas físico das TVs-alvo. Todos os vw/vh e o
 *  scale são referenciados a este número. NÃO é config por device — é a base
 *  fixa 1920x1080 (Full HD) que o appinfo/config.xml dos wrappers declara. */
export const DESIGN_WIDTH = 1920

/** Altura de DESIGN do mesmo canvas. Par do DESIGN_WIDTH — os dois são o
 *  `#root { width: 1920px; height: 1080px }` que o index.css fixa sob a tática B
 *  (o teste compara com o CSS e falha se divergirem). */
export const DESIGN_HEIGHT = 1080

/** UA-platforms que são TV. Espelha TV_PLATFORMS de surface.ts — mantidas
 *  separadas de propósito: surface.ts decide tv vs mobile (produto); aqui é só
 *  "é uma TV?" pra gate da escala de canvas (infra de layout). */
const TV_PLATFORMS = new Set(['tizen', 'webos', 'androidtv', 'firetv', 'vidaa'])

/**
 * A plataforma (por UA) é uma TV real? Fonte ÚNICA da lista TV_PLATFORMS neste
 * módulo — consumida tanto pelo gate da escala de canvas (isPackagedTv, que
 * ainda exige file://) quanto pelo gate da escala de UI lean-back
 * (applyTvUiScale, que NÃO exige file:// — Android TV/Fire via http também são
 * TV). Manter uma única cópia do Set evita divergência.
 *
 * `platform` é injetável pra teste puro; default lê detectPlatform() real.
 */
export function isTvPlatform(platform: string = detectPlatform()): boolean {
  return TV_PLATFORMS.has(platform)
}

/** Tolerância (px) da divergência de innerWidth vs DESIGN_WIDTH pra considerar
 *  que a meta viewport foi IGNORADA. 8px é ~0,4% de 1920 (imperceptível) e
 *  absorve o jitter sub-pixel que alguns WebViews de TV reportam por DPI
 *  scaling residual — evita falso positivo que armaria o fallback à toa num
 *  firmware que na prática pegou a meta viewport. Firmwares que ignoram a meta
 *  reportam viewports redondos (960/1024/1280), MUITO acima dessa tolerância. */
export const VIEWPORT_TOLERANCE_PX = 8

/** Subconjunto de `window.location` que precisamos — facilita testar sem DOM.
 *  `file:` = empacotado (mesmo sinal usado por router-basename.ts). */
export interface LocationLike {
  protocol: string
}

/**
 * É TV EMPACOTADA? Dois sinais, AMBOS necessários:
 *  1. protocolo `file:` — o WebView carregou o index.html do diretório de
 *     instalação do .ipk/.wgt (empacotado). Web (http/https) nunca é file://.
 *  2. plataforma de TV (TV_PLATFORMS) — descarta qualquer file:// que não seja
 *     TV (ex: abrir o dist localmente no navegador do PC pra debug).
 *
 * Ambos garantem que a camada 2 NUNCA arma no web (protocolo http/https) nem no
 * mobile — só no cenário exato do bug. `platform` é injetável pra teste puro;
 * default lê detectPlatform() real.
 */
export function isPackagedTv(loc: LocationLike, platform: string = detectPlatform()): boolean {
  const protocol = (loc.protocol || '').toLowerCase()
  if (protocol !== 'file:') return false
  return isTvPlatform(platform)
}

/**
 * Fator de escala do canvas a partir da largura de viewport MEDIDA.
 *
 * Contrato (travado com o de-risk do ux-tv):
 *  - `scale = measuredWidth / DESIGN_WIDTH` — SEMPRE nessa direção, NUNCA o
 *    inverso. Viewport lógico MENOR que a física (measured < 1920) → scale < 1
 *    → o wrapper de 1920px fixo ENCOLHE pra caber no viewport real (mata o
 *    "espremido no centro"). Inverter (1920/measured) estufaria o wrapper pra
 *    fora da viewport — bug clássico de off-by-inversion que passa no review
 *    visual e só aparece no device. Este teste trava a direção.
 *  - divergência DENTRO da tolerância (a meta viewport pegou) → retorna 1
 *    (NO-OP: fallback não arma).
 *  - measuredWidth inválido (0/NaN/negativo) → 1 (seguro: não escala com dado
 *    ruim; melhor "espremido" que quebrado).
 *
 * @returns fator ∈ (0, 1]. 1 = sem escala (caso normal / dado inválido).
 */
export function computeViewportScale(
  measuredWidth: number,
  designWidth: number = DESIGN_WIDTH,
  tolerancePx: number = VIEWPORT_TOLERANCE_PX,
): number {
  if (!Number.isFinite(measuredWidth) || measuredWidth <= 0) return 1
  if (!Number.isFinite(designWidth) || designWidth <= 0) return 1

  const divergence = Math.abs(measuredWidth - designWidth)
  // Dentro da tolerância: a meta viewport amarrou (ou o jitter é irrelevante).
  // NO-OP — não arma o fallback.
  if (divergence <= tolerancePx) return 1

  const scale = measuredWidth / designWidth
  // Guard defensivo: nunca > 1 (não estufamos além do design) nem <= 0.
  if (!Number.isFinite(scale) || scale <= 0) return 1
  return Math.min(scale, 1)
}

/** Viewport tuning que travamos no build de TV (camada 1) e reforçamos aqui na
 *  tática A. Fonte única do valor — mudou aqui, muda no reforço JS. */
const TV_VIEWPORT_CONTENT = 'width=1920, initial-scale=1.0, user-scalable=no'

/** Attribute-gate geral: marca que estamos numa TV empacotada. Informativo/CSS
 *  hook; NÃO liga transform sozinho. */
const PACKAGED_TV_ATTR = 'data-packaged-tv'

/** Attribute-gate da TÁTICA B: só é setado quando o fallback de escala armou
 *  (transform: scale ativo no #root). É este — e só este — que dispara o
 *  transform e a exceção do TrialOverlay no CSS (ver index.css).
 *
 *  EXPORTADO porque não é só um hook de CSS: qualquer JS que MEÇA o DOM precisa
 *  saber que está sob transform (ver isCanvasScaled / lib/card-metrics.ts). */
export const PACKAGED_TV_SCALED_ATTR = 'data-packaged-tv-scaled'

/**
 * A TÁTICA B está ARMADA? (o #root está sob `transform: scale(<1)`.)
 *
 * ===== POR QUE ISTO EXISTE (o bug que o review pegou) =====
 * Sob a tática B, o DOM passa a mentir pra duas fontes que o layout em JS usava:
 *  1. `getBoundingClientRect()` devolve px VISUAIS (já multiplicados pelo scale),
 *     não px de LAYOUT. Um grid de 1728px de layout mede 1152 num scale de 0.667.
 *  2. `window.innerWidth` devolve o viewport REAL do firmware (1280), não o canvas
 *     de DESIGN (1920) em que o conteúdo é efetivamente desenhado.
 * Consequência medida: `floor(1152 / 280)` = 4 colunas em vez de 6, e o
 * ResizeObserver NÃO re-dispara (transform não muda o tamanho de layout) → a
 * grade fica presa com ~1/3 da tela vazio. Quem mede layout tem que usar métrica
 * de LAYOUT (`clientWidth`/`offsetWidth`, imunes a transform) e, quando precisar
 * do viewport, usar o canvas de DESIGN — é o que este gate habilita.
 *
 * Não confundir com `data-packaged-tv` (informativo, marcado sempre que é TV
 * empacotada): SÓ este atributo significa "há transform ativo".
 */
export function isCanvasScaled(root?: Element | null): boolean {
  try {
    const el =
      root ?? (typeof document !== 'undefined' ? document.documentElement : null)
    return !!el && el.hasAttribute(PACKAGED_TV_SCALED_ATTR)
  } catch {
    return false
  }
}

/** Custom property consumida pelo CSS: transform: scale(var(--viewport-scale)).
 *  Default 1 no tokens.css (packages/ui). Sobrescrita aqui só na tática B. */
const VIEWPORT_SCALE_VAR = '--viewport-scale'

// ===== GATE DE TV (base lean-back) — o MULTIPLICADOR morreu, a BASE é rebaseada =====
//
// HISTÓRIA (por que este bloco encolheu):
// A 1ª tentativa de resolver o "conteúdo pequeno boiando num canvas 1920" foi um
// MULTIPLICADOR global: --ui-scale = clamp(innerWidth/1280, 1, 1.4), setado inline
// no :root. Não resolve, e ATRAPALHA:
//  - ele multiplicava só os TOKENS (fonte/espaço/raio) e NÃO a geometria em px dos
//    .module.css → o texto crescia 1.4x DENTRO de um card do mesmo tamanho;
//  - o viewport de TV é SEMPRE 1920 (os wrappers declaram o canvas), então o clamp
//    dava sempre o mesmo 1.4 constante — um "responsivo" que nunca respondia;
//  - papéis diferentes precisavam de fatores diferentes (título de card 2.3x, card
//    1.4x): um fator só não acerta os dois.
// A frente de densidade então REBASEOU os tokens pra 1920 (valores medidos pra 3m,
// em apps/tv/src/index.css sob html[data-tv-scale]) e declarou `--ui-scale: 1`.
//
// O BUG QUE O REVIEW PEGOU: a declaração `html[data-tv-scale] { --ui-scale: 1 }` era
// INERTE, porque este módulo continuava fazendo `root.style.setProperty('--ui-scale',
// '1.4')` — e style INLINE vence qualquer seletor de folha. Resultado medido no
// device: --space-3 = 16.8px (12 × 1.4) em vez de 12 → a célula do grid virava
// 256 + 2×16.8 = 289.6 → floor(1728/289.6) = 5 COLUNAS, não as 6 do contrato do
// ux-tv. Escala em dobro: a base JÁ estava rebaseada.
//
// AGORA: applyTvUiScale() só MARCA o gate (html[data-tv-scale]) e garante que o
// --ui-scale fica NEUTRO na TV (nenhum inline). O multiplicador continua existindo
// no tokens.css (calc(... * var(--ui-scale, 1))) porque o WEB tem viewport variável
// e pode querer usá-lo — lá o default é 1 e nada o sobrescreve. Morre só na TV, por
// capability, que é exatamente o que o review pediu.
//
// INDEPENDÊNCIA das duas escalas que sobram:
//  - --viewport-scale (tática B): transform: scale NO CONTAINER (#root), só quando
//    o firmware IGNORA a meta viewport. Encolhe (<1). Ver isCanvasScaled().
//  - --font-scale (acessibilidade do usuário: small .9 ... xlarge 1.3): multiplica
//    as fontes, e a caixa de info do card DERIVA dela (--card-vod-info-h).

/** Attribute-gate da BASE de TV. SÓ o app de TV seta (no boot, quando a plataforma
 *  é TV real por UA). É ele que liga o bloco `html[data-tv-scale]` do
 *  apps/tv/src/index.css, onde vivem os tokens rebaseados pra 1920 (tipografia,
 *  geometria de card, safe area). Site/admin NUNCA setam → base do tokens.css →
 *  zero regressão fora da TV. */
const TV_SCALE_ATTR = 'data-tv-scale'

/** Custom property que os tokens compartilhados ainda multiplicam via
 *  calc(... x var(--ui-scale, 1)). Default 1 no tokens.css; `1` explícito no bloco
 *  html[data-tv-scale] do index.css. Este módulo NUNCA a seta inline — se voltar a
 *  setar, o inline vence a folha e a escala volta a dobrar (ver o bloco acima). */
const UI_SCALE_VAR = '--ui-scale'

/**
 * Marca o gate da base de TV no boot. NO-OP se não for TV real (por UA).
 *
 * Fluxo (o que sobrou depois que o multiplicador morreu):
 *  1. Gate: só segue se `isTvPlatform()` (UA de TV, QUALQUER protocolo — cobre
 *     empacotado file:// e Android TV/Fire via http). Web desktop/mobile nunca
 *     passa aqui → base do tokens.css intacta → zero regressão.
 *  2. Marca `html[data-tv-scale]` → o index.css troca a BASE inteira (tipografia,
 *     card, safe area) pelos valores medidos pra 1920. Sem fator nenhum.
 *  3. NEUTRALIZA o --ui-scale: remove qualquer valor INLINE (higiene contra estado
 *     pendurado de HMR/reload/boot anterior). Sem inline, quem manda é o
 *     `html[data-tv-scale] { --ui-scale: 1 }` da folha — e a escala não dobra.
 *
 * "Verdade no boot": não reage a resize (mesmo princípio de resolveSurface /
 * applyPackagedTvScale). Idempotente e à prova de erro — nunca derruba o boot.
 */
export function applyTvUiScale(): void {
  try {
    if (typeof window === 'undefined' || typeof document === 'undefined') return
    if (!isTvPlatform()) return

    const root = document.documentElement
    // NUNCA setar valor aqui: style inline venceria o seletor de folha e a escala
    // dobraria (o bug do review). Só limpamos o que um boot anterior possa ter
    // deixado — o valor efetivo tem que vir da FOLHA.
    root.style.removeProperty(UI_SCALE_VAR)
    root.setAttribute(TV_SCALE_ATTR, '')
  } catch {
    // Rede de segurança — nunca pode quebrar o boot. Engole tudo.
  }
}

/**
 * TÁTICA A — reforça a meta viewport via JS e devolve o innerWidth APÓS um
 * reflow forçado.
 *
 * Por quê reforçar por JS se o build já injeta a meta: alguns WebViews de TV
 * IGNORAM a meta ESTÁTICA do HTML mas REAGEM à mesma meta setada por script no
 * boot (ordem de parse vs. init do layout engine). Reescrever o `content` é
 * idempotente e barato — no caso comum (meta já pegou) não muda nada.
 *
 * Reflow forçado (condição 1 do gate): entre setar a meta e reler innerWidth,
 * forçamos o layout a recomputar. Sem isso, WebViews que aplicam o viewport de
 * forma ASSÍNCRONA devolveriam innerWidth ESTALE → armaríamos a tática B
 * (transform) à toa num firmware que responderia bem só ao reforço. Lemos
 * `documentElement.clientWidth`/`offsetHeight` (property que força sync layout).
 *
 * @returns innerWidth medido após o reforço+reflow (px). Nunca lança.
 */
function reinforceViewportAndMeasure(doc: Document): number {
  try {
    let meta = doc.querySelector('meta[name="viewport"]')
    if (!meta) {
      meta = doc.createElement('meta')
      meta.setAttribute('name', 'viewport')
      doc.head.appendChild(meta)
    }
    // Idempotente: reescreve mesmo que o valor já esteja igual — alguns engines
    // só reprocessam o viewport num setAttribute explícito.
    meta.setAttribute('content', TV_VIEWPORT_CONTENT)

    // Força reflow SÍNCRONO: ler uma métrica de layout invalida o layout
    // pendente e obriga o engine a recomputar antes de devolver o valor.
    // (offsetHeight/clientWidth são "layout-thrashing triggers" clássicos.)
    void doc.documentElement.offsetHeight
    void doc.documentElement.clientWidth
  } catch {
    // DOM indisponível/parcial: segue com a medição do window abaixo.
  }
  return readInnerWidth()
}

/** Lê window.innerWidth com fallbacks. 0 se indisponível → computeViewportScale
 *  trata como inválido (retorna 1, não arma). */
function readInnerWidth(): number {
  if (typeof window === 'undefined') return 0
  const w = window.innerWidth
  if (typeof w === 'number' && w > 0) return w
  // Fallback: documentElement.clientWidth (alguns WebViews antigos preferem).
  const cw = window.document?.documentElement?.clientWidth
  return typeof cw === 'number' && cw > 0 ? cw : 0
}

/**
 * Aplica a defesa da CAMADA 2 no boot. NO-OP se não for TV empacotada.
 *
 * Fluxo:
 *  1. Gate: só segue se `isPackagedTv` (file:// + plataforma TV). Marca
 *     `html[data-packaged-tv]` (hook informativo).
 *  2. TÁTICA A: reforça a meta viewport por JS e remede innerWidth com reflow
 *     forçado. Se innerWidth convergiu (dentro da tolerância) → meta pegou,
 *     FIM (sem transform, sem containing block novo — o melhor caso).
 *  3. TÁTICA B (último recurso): a meta segue ignorada. Seta
 *     `--viewport-scale = innerWidth/1920` no :root e marca
 *     `html[data-packaged-tv-scaled]` → o CSS aplica transform: scale() no
 *     #root e a exceção 100vw/100vh do TrialOverlay (paywall NUNCA pode vazar).
 *
 * "Verdade no boot": não reage a resize (trocar no meio remontaria/reflow
 * caótico; mesmo princípio de resolveSurface). Idempotente e à prova de erro:
 * qualquer exceção é engolida — a camada 2 NUNCA pode derrubar o boot.
 */
export function applyPackagedTvScale(): void {
  try {
    if (typeof window === 'undefined' || typeof document === 'undefined') return
    if (!isPackagedTv(window.location)) return

    const root = document.documentElement
    root.setAttribute(PACKAGED_TV_ATTR, '')

    // TÁTICA A: reforço da meta viewport + remedição com reflow forçado.
    const measured = reinforceViewportAndMeasure(document)
    const scale = computeViewportScale(measured)

    // scale === 1 significa: convergiu (meta pegou) OU dado inválido. Nos dois
    // casos NÃO armamos o transform — o melhor caso e o caso seguro.
    if (scale >= 1) {
      // Higiene: garante que um estado B de um boot anterior (HMR/reload) não
      // fique pendurado. Em produção é no-op (attr nunca foi setado).
      root.removeAttribute(PACKAGED_TV_SCALED_ATTR)
      root.style.removeProperty(VIEWPORT_SCALE_VAR)
      return
    }

    // TÁTICA B: a meta foi ignorada mesmo após o reforço. Fallback de escala.
    root.style.setProperty(VIEWPORT_SCALE_VAR, String(scale))
    root.setAttribute(PACKAGED_TV_SCALED_ATTR, '')
  } catch {
    // A camada 2 é rede de segurança — nunca pode quebrar o boot. Engole tudo.
  }
}
